<template>
  <div id="nomination-list" class="premium-dashboard">
    <overlay-loading-progress :LoadingFlag="OverlayLoadingFlag" />
    <!-- Background Floating Light Orbs -->
    <div class="glow-orb orb-1"></div>
    <div class="glow-orb orb-2"></div>

    <!-- Massive Hero Dashboard Header -->
    <v-card class="hero-card mb-6" flat>
      <div class="hero-glow-effect"></div>

      <!-- Floating Decorative Ambient Icons -->
      <v-icon class="deco-icon icon-card" color="rgba(255, 255, 255, 0.08)"
        >mdi-file-certificate-outline</v-icon
      >
      <v-icon class="deco-icon icon-receipt" color="rgba(255, 255, 255, 0.08)"
        >mdi-account-badge-outline</v-icon
      >
      <v-icon class="deco-icon icon-safe" color="rgba(255, 255, 255, 0.08)"
        >mdi-shield-star-outline</v-icon
      >

      <!-- Ocean Wave Background Animation -->
      <div class="waves-container">
        <svg
          class="waves"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          viewBox="0 24 150 28"
          preserveAspectRatio="none"
          shape-rendering="auto"
        >
          <defs>
            <path
              id="gentle-wave"
              d="M-160 44c30 0 58-18 88-18s58 18 88 18 58-18 88-18 58 18 88 18v44h-352z"
            />
          </defs>
          <g class="parallax">
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="0"
              fill="rgba(255, 255, 255, 0.05)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="3"
              fill="rgba(255, 255, 255, 0.08)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="5"
              fill="rgba(255, 255, 255, 0.03)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="7"
              fill="rgba(255, 255, 255, 0.07)"
            />
          </g>
        </svg>
      </div>

      <br />
      <v-row class="ma-0 align-center relative-content">
        <v-col cols="12" md="7" class="pa-6 pb-12">
          <div class="d-flex align-center title-container">
            <v-icon large color="white" class="mr-3 title-icon pulse-animation"
              >mdi-file</v-icon
            >
            <h1 class="hero-title animate-title">
              {{ PageTitle || "National / Zone Election Nominations" }}
            </h1>
          </div>
          <p class="hero-subtitle animate-subtitle">
            {{
              PageDescription ||
              "View, filter, verify, and manage member nomination applications."
            }}
          </p>
        </v-col>
        <v-col
          cols="12"
          md="5"
          class="pa-6 pb-12 text-md-right d-none d-sm-block"
        >
          <v-btn
            to="/nomination/manual-entry"
            color="white"
            light
            elevation="3"
            rounded
            large
            class="font-weight-bold mr-3"
          >
            <v-icon left color="primary">mdi-plus</v-icon>
            Nomination Manual Entry
          </v-btn>
          <v-btn
            v-if="refreshButtonFlag"
            large
            @click.prevent="refreshPageData"
            color="rgba(255,255,255,0.2)"
            dark
            elevation="0"
            rounded
            class="font-weight-bold"
            style="border: 1px solid rgba(255, 255, 255, 0.4)"
          >
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Summary Statistics Row -->
    <v-row class="mb-6" v-if="rows.length > 0">
      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-total"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Total Nominations</span>
              <h2 class="stat-value">{{ rows.length }}</h2>
            </div>
            <v-avatar color="rgba(21, 101, 192, 0.1)" size="48">
              <v-icon color="#1565C0">mdi-file</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-success"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Completed Nominations</span>
              <h2 class="stat-value">
                {{
                  rows.filter(
                    (item) =>
                      item.StatusTxt === "Completed" ||
                      item.VerifyStatusTxt === "Completed" ||
                      item.StatusTxt === "Approved" ||
                      item.VerifyStatusTxt === "Approved"
                  ).length
                }}
              </h2>
            </div>
            <v-avatar color="rgba(76, 175, 80, 0.1)" size="48">
              <v-icon color="#4caf50">mdi-check-circle-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-pending"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Pending Verification</span>
              <h2 class="stat-value">
                {{
                  rows.filter(
                    (item) =>
                      item.VerifyStatusTxt === "Pending" ||
                      item.StatusTxt === "Pending"
                  ).length
                }}
              </h2>
            </div>
            <v-avatar color="rgba(255, 152, 0, 0.1)" size="48">
              <v-icon color="#ff9800">mdi-clock-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-rev"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Special Types</span>
              <h2 class="stat-value">
                {{
                  rows.filter(
                    (item) =>
                      item.NominationTypeTxt &&
                      item.NominationTypeTxt !== "Normal"
                  ).length
                }}
              </h2>
            </div>
            <v-avatar color="rgba(30, 136, 229, 0.1)" size="48">
              <v-icon color="#1e88e5">mdi-certificate</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Section -->
    <v-card class="filter-card glass-card mb-6">
      <div class="filter-card-header d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-filter-variant</v-icon>
        <h3 class="filter-card-title">Search Parameters</h3>
      </div>

      <v-card-text class="pa-6">
        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="getTableRecords"
        >
          <v-row wrap align="center">
            <v-col cols="12" sm="6" md="2" class="py-2">
              <label class="filter-label">Year <small>(optional)</small></label>
              <v-autocomplete
                :reduce="(option) => option.value"
                v-model="JciYearCode"
                :rules="JciYearCodeRules"
                :items="JciYearCodeOptions"
                :loading="JciYearCodeOptionsLoading"
                placeholder="Select Year"
                dense
                clearable
                required
                outlined
                rounded
                class="premium-select"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="2" class="py-2">
              <label class="filter-label">Zone <small>(optional)</small></label>
              <v-autocomplete
                :reduce="(option) => option.value"
                v-model="ZoneCode"
                :items="ZoneCodeOptions"
                :loading="ZoneCodeOptionsLoading"
                placeholder="Select Zone"
                dense
                clearable
                required
                outlined
                rounded
                class="premium-select"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="py-2">
              <label class="filter-label">LO <small>(optional)</small></label>
              <v-autocomplete
                :reduce="(option) => option.value"
                v-model="LomCode"
                :rules="LomCodeRules"
                :items="LomCodeOptions"
                :loading="LomCodeOptionsLoading"
                placeholder="Select LO"
                dense
                clearable
                required
                outlined
                rounded
                class="premium-select"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="py-2">
              <label class="filter-label"
                >Nomination Designation <small>(optional)</small></label
              >
              <v-autocomplete
                :reduce="(option) => option.value"
                :items="NominationDesignationOptions"
                :rules="NominationDesignationRules"
                :loading="NominationDesignationOptionsLoading"
                v-model="NominationDesignation"
                :item-value="'value'"
                :item-text="'text'"
                placeholder="Select Designation"
                dense
                clearable
                required
                outlined
                rounded
                class="premium-select"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6" md="2" class="py-2">
              <label class="filter-label"
                >Nomination Type <small>(optional)</small></label
              >
              <v-autocomplete
                v-model="selectedNominationType"
                :items="nominationTypeList"
                placeholder="Select Type"
                dense
                outlined
                rounded
                clearable
                item-text="text"
                item-value="value"
                class="premium-select"
              ></v-autocomplete>
            </v-col>
          </v-row>

          <v-row wrap align="center">
            <v-col cols="12" class="py-2 text-right">
              <v-btn
                :disabled="!valid1"
                @click.prevent="getTableRecords"
                :loading="LoadingFlag"
                rounded
                large
                class="filter-btn btn-search white--text mr-2"
              >
                <v-icon left>mdi-magnify</v-icon>
                Search
              </v-btn>
              <v-btn
                @click.prevent="
                  resetForm();
                  JciYearCode = '';
                  ZoneCode = '';
                  LomCode = '';
                  NominationDesignation = '';
                  selectedNominationType = '';
                "
                rounded
                large
                outlined
                class="filter-btn btn-clear"
              >
                Reset
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Loading Shimmer State -->
    <v-card v-if="LoadingFlag" class="glass-card pa-6 mb-6">
      <v-skeleton-loader
        type="table-heading, table-thead, table-tbody, table-tfoot"
        class="premium-loader"
      ></v-skeleton-loader>
    </v-card>

    <!-- Data Table & Search Grid -->
    <v-card
      v-if="!LoadingFlag && rows.length > 0"
      class="table-container-card glass-card mb-6"
    >
      <v-card-title
        class="px-6 py-4 d-flex justify-content-between align-center flex-wrap"
      >
        <div class="d-flex align-center mb-2 mb-sm-0">
          <v-icon color="#1565C0" class="mr-2">mdi-database</v-icon>
          <span class="result-count-text"
            >{{ rows.length }} Nominations Found</span
          >
        </div>

        <div class="d-flex align-center flex-wrap">
          <json-excel
            v-if="rows.length > 0"
            :data="rows"
            :fields="ExcelFields"
            worksheet="My Worksheet"
            :name="ExcelFileName"
            class="mr-3"
          >
            <v-btn
              color="#1565C0"
              dark
              rounded
              small
              elevation="2"
              class="font-weight-bold"
            >
              <v-icon left small>mdi-file-excel</v-icon>
              Export Excel
            </v-btn>
          </json-excel>

          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Quick search table..."
            single-line
            hide-details
            dense
            outlined
            rounded
            class="global-table-search"
          ></v-text-field>
        </div>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-data-table
          class="premium-table"
          v-model="selected1"
          :search="search"
          :item-key="tableOptions1.ItemKey"
          :single-select="tableOptions1.SingleSelectFlag"
          :headers="tableColumns1"
          :items="rows"
          :items-per-page="tableOptions1.ItemsPerPage"
          :footer-props="tableOptions1.FooterProps"
        >
          <template v-slot:[`item.StatusTxt`]="{ item }">
            <v-chip
              small
              class="font-weight-bold status-chip"
              :color="item.StatusTxtColor || 'rgba(76, 175, 80, 0.1)'"
              :text-color="item.StatusTxtColor ? 'white' : '#2e7d32'"
            >
              {{ item.StatusTxt }}
            </v-chip>
          </template>

          <template v-slot:[`item.VerifyStatusTxt`]="{ item }">
            <v-chip
              small
              class="font-weight-bold status-chip"
              :color="item.VerifyStatusTxtColor || 'rgba(30, 136, 229, 0.1)'"
              :text-color="item.VerifyStatusTxtColor ? 'white' : '#1565c0'"
            >
              {{ item.VerifyStatusTxt }}
            </v-chip>
          </template>

          <template v-slot:[`item.PresidentStatusTxt`]="{ item }">
            <v-chip
              small
              class="font-weight-bold status-chip"
              :color="item.PresidentStatusTxtColor || 'rgba(255, 152, 0, 0.1)'"
              :text-color="item.PresidentStatusTxtColor ? 'white' : '#e65100'"
            >
              {{ item.PresidentStatusTxt }}
            </v-chip>
          </template>

          <template v-slot:[`item.NominationTypeTxt`]="{ item }">
            <v-chip
              small
              class="font-weight-bold status-chip"
              :color="item.NominationTypeColor || 'rgba(156, 39, 176, 0.1)'"
              :text-color="item.NominationTypeColor ? 'white' : '#7b1fa2'"
            >
              {{ item.NominationTypeTxt }}
            </v-chip>
          </template>

          <template v-slot:[`item.ResultStatusTxt`]="{ item }">
            <v-chip
              small
              class="font-weight-bold status-chip"
              :color="item.ResultStatusColor || 'rgba(0, 150, 136, 0.1)'"
              :text-color="item.ResultStatusColor ? 'white' : '#00695c'"
            >
              {{ item.ResultStatusTxt }}
            </v-chip>
          </template>

          <template v-slot:[`item.AgeProofStatusTxt`]="{ item }">
            <v-chip
              small
              class="font-weight-bold status-chip"
              :color="item.AgeProofStatusColor || 'rgba(121, 85, 72, 0.1)'"
              :text-color="item.AgeProofStatusColor ? 'white' : '#4e342e'"
            >
              {{ item.AgeProofStatusTxt }}
            </v-chip>
          </template>

          <template v-slot:[`item.MemberImage`]="{ item }">
            <v-avatar size="46" class="elevation-2 my-1">
              <img :src="item.MemberImage" alt="Member Image" />
            </v-avatar>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center">
              <v-tooltip bottom v-if="item.PreviewFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    class="action-btn-view mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click="previewData(item)"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>Preview</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.ApprovalFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="success"
                    class="action-btn-view mr-1"
                    v-bind="attrs"
                    v-on="on"
                    @click="approveData(item)"
                  >
                    <v-icon small>mdi-check-circle</v-icon>
                  </v-btn>
                </template>
                <span>Approve (Co-option only)</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.ApplicationFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="error"
                    class="action-btn-view mr-1"
                    :href="item.ApplicationPath"
                    target="_blank"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon small>mdi-file-pdf-box</v-icon>
                  </v-btn>
                </template>
                <span>Download</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.ApplicationFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="success"
                    class="action-btn-view mr-1"
                    v-bind="attrs"
                    v-on="on"
                    :loading="item.RegenerateLoadingFlag"
                    @click="regenerateApplication(item)"
                  >
                    <v-icon small>mdi-refresh</v-icon>
                  </v-btn>
                </template>
                <span>Regenerate PDF</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="addRecordPrompt" persistent max-width="600px">
      <add-nomination-online-payment
        pageDescription="Add Nomination Online Payment"
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt"
        v-if="addRecordPrompt"
      ></add-nomination-online-payment>
    </v-dialog>

    <v-dialog v-model="previewRecordPrompt" max-width="75%">
      <nomination-preview
        pageDescription=" Nomination Details"
        :showDialog="previewRecordPrompt"
        @hideDialog="hidePreviewRecordPrompt($event)"
        :row="selectedData"
        v-if="previewRecordPrompt"
      ></nomination-preview>
    </v-dialog>

    <v-dialog v-model="coOptionApprovalPrompt" max-width="600px">
      <co-option-approval
        pageDescription="Approve Co-Option Nomination"
        :coOptionApprovalPrompt="coOptionApprovalPrompt"
        @hideDialog="hideCoOptionApprovalPrompt($event)"
        :row="selectedData"
        v-if="coOptionApprovalPrompt"
      ></co-option-approval>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import NominationPreview from "@/view/pages/erp/events/nomination/NominationPreview.vue";
import CoOptionApproval from "@/view/pages/erp/events/nomination/CoOptionApproval.vue";

export default {
  mixins: [common],
  components: {
    NominationPreview,
    CoOptionApproval,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      OverlayLoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: 0,

      selectedNominationType: "",
      nominationTypeList: [
        { text: "Select Type", value: "" },
        { text: "Normal", value: 1 },
        { text: "Floor Nomination", value: 2 },
        { text: "Co-option", value: 3 },
      ],

      coOptionApprovalPrompt: false,
      refreshButtonFlag: true,
      floatButtonFlag: false,
      selected1: "",
      rows: [],
      tableColumns1: [],
      tableOptions1: {},
      selected: [],
      search: "",
      CurrentYearId: 0,
      previewRecordPrompt: false,

      DownloadFlag: false,
      ExcelFileName: "",
      ExcelFields: "",

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",
      editRecordPrompt: false,

      editFlag: false,

      selectedData: {},
      addRecordPrompt: false,

      select: "Item 1",
      items: ["Item 1", "Item 2", "Item 3", "Item 4"],
      checkbox: false,

      SearchId: "",
      SearchIdRules: [(v) => !!v || "Search Type is required"],

      LoadingFlag: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",

      JciYearCodeRules: [],
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      ZoneCodeRules: [],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      LomCodeRules: [],
      LomCode: "",
      LomCodeOptions: [],
      LomCodeOptionsLoading: false,

      NominationDesignationOptions: [],
      NominationDesignationRules: [],
      NominationDesignationOptionsLoading: false,
      NominationDesignation: "",
      RoleId: "",
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.LoadingFlag = false;
      this.JciYearCodeOptionsLoading = false;
      if (
        this.JciYearCodeOptions &&
        this.JciYearCodeOptions.length > 2 &&
        this.JciYearCode
      ) {
        var currentId = parseInt(this.JciYearCode);
        var prevId = currentId - 1;
        this.JciYearCodeOptions = this.JciYearCodeOptions.filter(
          (opt) => opt.value == currentId || opt.value == prevId
        );
      }
    },
    JciYearCode: function () {
      console.log("watch JciYearCode");
      this.ZoneCode = "";
      this.LomCode = "";
      this.getZoneCodeOptions();
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.LoadingFlag = false;
      this.ZoneCodeOptionsLoading = false;
    },
    ZoneCode: function () {
      console.log("watch ZoneCode");
      // this.getRegionCodeOptions();
      this.LomCode = "";
      this.getLomCodeOptions();
    },
    LomCodeOptions: function () {
      console.log("watch LomCodeOptions");
      this.LoadingFlag = false;
      this.LomCodeOptionsLoading = false;
    },
    NominationDesignation: function () {
      console.log("watch NominationDesignation");
      this.NominationDesignationLoading = false;
      var temp_code = this.NominationDesignation;
      console.log({ temp_code });
    },
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var PagePermissionFlag = tr.PagePermissionFlag;
        console.log({ PagePermissionFlag });

        if (!PagePermissionFlag) {
          this.$router.push("/access-denied");
        }

        var PagePermissionTxt = tr.PagePermissionTxt;
        console.log({ PagePermissionTxt });

        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var PreviousJciYearId = CurrentJciYearId - 1;
        var PreviousYearName = YearName - 1;

        this.JciYearCodeOptions = [
          { text: YearName.toString(), value: CurrentJciYearId },
          { text: PreviousYearName.toString(), value: PreviousJciYearId },
        ];

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = NextJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        // this.LomCode = LomId;

        var RoleId = this.$session.get("RoleId");
        RoleId = RoleId == (null || undefined) ? "" : RoleId;
        // RoleId = 4;
        this.RoleId = RoleId;

        this.pageData();
      }
    },
  },
  methods: {
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "nomination",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
      // this.getTableRecords();
    },
    hidePreviewRecordPrompt(flag) {
      console.log("hidePreviewRecordPrompt called");
      console.log({ flag });
      this.previewRecordPrompt = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    hideCoOptionApprovalPrompt(flag) {
      console.log("hideCoOptionApprovalPrompt called");
      this.coOptionApprovalPrompt = false;
      console.log({ flag });
      if (flag) {
        this.getTableRecords();
      }
    },
    pageData() {
      console.log("pageData called");
      this.getJciYearCodeOptions();
      this.getZoneCodeOptions();
      this.getNominationDesignationOptions();
    },
    hideAddRecordPrompt(flag) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log(flag);
      if (flag) {
        this.getTableRecords();
      }
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      // console.log(flag);
      // if (flag) {
      this.getTableRecords();
      // }
    },
    previewData(tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    approveData(tr) {
      console.log("approveData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.coOptionApprovalPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to approve";
        this.toast("error", message);
      }
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one member to edit";
        this.toast("error", message);
      }
    },
    getJciYearCodeOptions() {
      console.log("getJciYearCodeOptions called");
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getNominationDesignationOptions() {
      console.log("getNominationDesignationOptions called");
      const selectbox1_source = "NominationDesignation";
      const selectbox1_destination = "NominationDesignationOptions"; // must match autocomplete items
      const selectbox1_url = "api/nomination-designation/options";
      const selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      var areaCode = this.AreaCode;
      console.log("yearCode=" + yearCode + ", areaCode=" + areaCode);
      if (yearCode != "") {
        this.LoadingFlag = true;
        this.ZoneCodeOptionsLoading = true;
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (yearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getLomCodeOptions() {
      console.log("getLomCodeOptions called");
      var YearCode = this.JciYearCode;
      var ZoneCode = this.ZoneCode;
      var RegionCode = this.RegionCode;
      console.log(
        "YearCode=" +
          YearCode +
          ", ZoneCode=" +
          ZoneCode +
          ", RegionCode=" +
          RegionCode
      );
      if (YearCode != "") {
        this.LoadingFlag = true;
        this.LomCodeOptionsLoading = true;
        var selectbox1_source = "LomCode";
        var selectbox1_destination = "LomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/nomination/lists";
      var upload = {
        UserInterface: 1,
        Zone: this.ZoneCode,
        Lom: this.LomCode,
        Designation: this.NominationDesignation,
        NominationType: this.selectedNominationType,
        Year: this.JciYearCode,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = "";
      var successTxt = "";
      var errorTxt = "";
      var flag = 0;

      this.rows = [];
      this.ResultFlag = false;
      this.LoadingFlag = true;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.ResultFlag = true;
          thisIns.LoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          output = response.data.output;
          records = response.data.records;
          console.log("output=" + output + ", flag=" + flag);
          successTxt = response.data.success;
          errorTxt = response.data.error;
          console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

          var options = records.TableOptions;
          thisIns.tableOptions1 = options;

          if (flag == 1) {
            thisIns.rows = records.TableData;
            thisIns.tableColumns1 = records.TableHeader;
            thisIns.tableOptions1 = records.TableOptions;

            thisIns.DownloadFlag = options.ExcelDownloadFlag;
            thisIns.ExcelFileName = options.ExcelFileName;
            thisIns.ExcelFields = options.ExcelFields;

            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.ResultFlag = true;
          thisIns.LoadingFlag = false;
        });
    },
    deleteConfirmAlert(tr) {
      console.log("deleteConfirmAlert called");
      var message = "";
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "<br/> Member Name <b> " + tr.MemberName + "</b>";
        htmlTxt += "<br/> Designation Name  <b> " + tr.DesignationName + "</b>";

        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.deleteData(tr.NominationMember);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        if (n1 == 0) {
          message += "Kindly select one record to delete. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    deleteData(NominationMember) {
      console.log("deleteData is called");

      console.log("NominationMember=" + NominationMember);

      if (NominationMember > 0) {
        this.DeleteLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/nomination-member/destroy";
        var upload = {
          UserInterface: 1,
          NominationMember: NominationMember,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.DeleteLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.getTableRecords();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
            thisIns.DeleteLoadingFlag = false;
          });
      } else {
        var message = "Nomination Member Id should not be empty. ";
        this.sweetAlert("error", message, false);
      }
    },
    regenerateApplication(item) {
      console.log("regenerateApplication is called");

      var NominationMember = item.NominationMemberId;
      var NominationId = item.NominationId;
      var MemberId = item.MemberId;

      console.log("NominationMember=" + NominationMember);
      console.log("NominationId=" + NominationId);
      console.log("MemberId=" + MemberId);

      if (NominationMember > 0 && NominationId > 0 && MemberId > 0) {
        this.$set(item, "RegenerateLoadingFlag", true);
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/nomination/regeneration-application";
        var upload = {
          UserInterface: 1,
          Nomination: NominationId,
          NominationMember: NominationMember,
          Member: MemberId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));
            thisIns.$set(item, "RegenerateLoadingFlag", false);
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.getTableRecords();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.$set(item, "RegenerateLoadingFlag", false);
            thisIns.OverlayLoadingFlag = false;
            thisIns.LoadingFlag = false;
            thisIns.DeleteLoadingFlag = false;
          });
      } else {
        var message = "";
        if (NominationMember == 0 && NominationMember != "") {
          message += "Nomination Member Id should not be empty. ";
        }
        if (NominationId == 0 && NominationId != "") {
          message += "Nomination Id should not be empty. ";
        }
        if (MemberId == 0 && MemberId != "") {
          message += "Member Id should not be empty. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style scoped>
/* Premium Dashboard Container */
.premium-dashboard {
  position: relative;
  min-height: 100vh;
}

/* Background Glowing Orbs */
.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  z-index: 0;
  pointer-events: none;
}

.orb-1 {
  width: 350px;
  height: 350px;
  background: rgba(21, 101, 192, 0.06);
  top: -50px;
  left: -50px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: rgba(66, 165, 245, 0.05);
  top: 300px;
  right: -100px;
}

/* Hero Header Card */
.hero-card {
  background: linear-gradient(
    135deg,
    #1565c0 0%,
    #1a73e8 45%,
    #42a5f5 100%
  ) !important;
  border-radius: 20px !important;
  color: white;
  box-shadow: 0 16px 40px rgba(21, 101, 192, 0.25) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.8px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.95;
  margin-top: 6px;
  max-width: 600px;
  font-weight: 400;
}

/* Glassmorphism General Cards styling */
.glass-card {
  background: rgba(255, 255, 255, 0.82) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.7) !important;
  border-radius: 20px !important;
  box-shadow: 0 10px 30px rgba(33, 33, 33, 0.04) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* Summary Statistics Cards */
.stat-card {
  padding: 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(21, 101, 192, 0.12) !important;
}

.stat-card-glow {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  filter: blur(40px);
  top: -40px;
  right: -40px;
  opacity: 0.15;
}

.orb-total {
  background-color: #1565c0;
}

.orb-rev {
  background-color: #4caf50;
}

.orb-success {
  background-color: #1e88e5;
}

.orb-pending {
  background-color: #ff9800;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #78909c;
  letter-spacing: 0.8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 800;
  color: #263238;
  margin-top: 4px;
  letter-spacing: -0.5px;
}

/* Advanced Filter Card header gradient */
.filter-card-header {
  background: linear-gradient(90deg, #1565c0 0%, #1e88e5 100%);
  padding: 16px 24px;
  color: white;
}

.filter-card-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 0;
}

.filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #455a64;
  margin-bottom: 6px;
  display: block;
}

/* Autocomplete/Select box rounded controls */
.premium-select >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.1) !important;
  background-color: white !important;
  transition: all 0.3s ease !important;
}

.premium-select >>> .v-input__control > .v-input__slot:hover {
  border-color: rgba(21, 101, 192, 0.35) !important;
}

.premium-select.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #1565c0 !important;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.15) !important;
}

/* Filter action buttons */
.filter-btn {
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.btn-clear {
  color: #455a64 !important;
  border: 1.5px solid rgba(69, 90, 100, 0.2) !important;
}

.btn-clear:hover {
  background-color: rgba(69, 90, 100, 0.05) !important;
  border-color: #455a64 !important;
}

.btn-search {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%) !important;
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.3) !important;
  border: none !important;
}

.btn-search:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(21, 101, 192, 0.45) !important;
}

/* Skeleton Loaders and Empty States styling */
.premium-loader {
  background: transparent !important;
}

/* Data grid container and table styles */
.table-container-card {
  overflow: hidden;
}

.result-count-text {
  font-size: 16px;
  font-weight: 700;
  color: #263238;
}

/* Premium global table search text field */
.global-table-search {
  max-width: 280px;
}

.global-table-search >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.12) !important;
  background-color: rgba(21, 101, 192, 0.01) !important;
}

.global-table-search.v-input--is-focused
  >>> .v-input__control
  > .v-input__slot {
  border-color: #1565c0 !important;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.12) !important;
  background-color: white !important;
}

/* Real premium tables */
.premium-table >>> table {
  border-collapse: separate;
  border-spacing: 0;
}

.premium-table >>> thead > tr > th {
  background-color: rgba(21, 101, 192, 0.04) !important;
  color: #1565c0 !important;
  font-weight: 700 !important;
  font-size: 15px !important;
  border-bottom: 2px solid rgba(21, 101, 192, 0.08) !important;
}

.premium-table >>> tbody > tr {
  transition: background-color 0.2s ease;
}

.premium-table >>> tbody > tr:hover {
  background-color: rgba(21, 101, 192, 0.03) !important;
}

.premium-table >>> tbody > tr > td {
  border-bottom: 1px solid rgba(21, 101, 192, 0.04) !important;
  font-size: 15px !important;
  color: #37474f;
  padding: 12px 16px !important;
}

.status-chip {
  font-weight: bold;
}

/* Ocean Wave Styling */
.waves-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  min-height: 40px;
  max-height: 60px;
  z-index: 1;
}

.waves {
  position: relative;
  width: 100%;
  height: 40px;
  margin-bottom: -7px;
}

.parallax > use {
  animation: move-forever 25s cubic-bezier(0.55, 0.5, 0.45, 0.5) infinite;
}

.parallax > use:nth-child(1) {
  animation-delay: -2s;
  animation-duration: 7s;
}

.parallax > use:nth-child(2) {
  animation-delay: -3s;
  animation-duration: 10s;
}

.parallax > use:nth-child(3) {
  animation-delay: -4s;
  animation-duration: 13s;
}

.parallax > use:nth-child(4) {
  animation-delay: -5s;
  animation-duration: 20s;
}

@keyframes move-forever {
  0% {
    transform: translate3d(-90px, 0, 0);
  }

  100% {
    transform: translate3d(85px, 0, 0);
  }
}

/* Hero header animations & shapes */
.hero-glow-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  z-index: 0;
  pointer-events: none;
}

.hero-glow-effect::after {
  content: "";
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.15) 30%,
    rgba(255, 255, 255, 0) 60%
  );
  transform: skewX(-25deg);
  animation: shineSweep 6s infinite ease-in-out;
}

.relative-content {
  position: relative;
  z-index: 2;
}

/* Decorative background icons */
.deco-icon {
  position: absolute !important;
  z-index: 1;
  pointer-events: none;
}

.icon-card {
  font-size: 110px !important;
  right: 20%;
  top: -20px;
  transform: rotate(15deg);
  animation: floatDeco 12s infinite ease-in-out;
}

.icon-receipt {
  font-size: 80px !important;
  left: 10%;
  bottom: -15px;
  transform: rotate(-10deg);
  animation: floatDeco 15s infinite ease-in-out alternate;
}

.icon-safe {
  font-size: 90px !important;
  right: 5%;
  bottom: -10px;
  transform: rotate(20deg);
  animation: floatDeco 10s infinite ease-in-out;
}

/* Specific text and icon animations */
.animate-title {
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-subtitle {
  opacity: 0;
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.15s forwards;
}

.pulse-animation {
  animation: pulseIcon 3s infinite ease-in-out;
}

/* Keyframes definitions */
@keyframes shineSweep {
  0% {
    left: -120%;
  }

  15%,
  100% {
    left: 120%;
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes floatDeco {
  0%,
  100% {
    transform: translateY(0) rotate(15deg) scale(1);
  }

  50% {
    transform: translateY(-12px) rotate(20deg) scale(1.03);
  }
}

@keyframes pulseIcon {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0px rgba(255, 255, 255, 0));
  }

  50% {
    transform: scale(1.08);
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.6));
  }
}

.action-btn-view {
  transition: transform 0.2s ease;
}

.action-btn-view:hover {
  transform: scale(1.18);
  background: rgba(21, 101, 192, 0.08) !important;
}
</style>